import type {
  ConditionalFieldCalculationAction,
  ConditionalFieldVisibilityAction,
  ConditionalFieldMaskAction,
  VisibilityActionType,
  ConditionalPageAction,
  Conditional,
  ConditionalAction,
  ConditionalTerm,
} from '@restore/dynamic-forms';

export type JotformActionType = 'visibility' | 'calculation' | 'pageNavigation';

export type JotformVisibility =
  | 'Show'
  | 'ShowMultiple'
  | 'Hide'
  | 'HideMultiple'
  | 'Require'
  | 'Unrequire'
  | 'RequireMultiple'
  | 'UnrequireMultiple'
  | 'Disable'
  | 'Enable'
  | 'Mask';

export type JotformConditionalAction = {
  id: string;
  visibility?: JotformVisibility;
  field?: string;
  fields?: string[];
  isError: boolean;
  mask?: string;
  replaceText?: string;
  readOnly?: boolean;
  newCalculationType?: boolean;
  allowZeroCopy?: boolean;
  useCommasForDecimals?: boolean;
  operands?: string; // Convert to number or array of numbers (split by comma) or something like this? {14}[{16}{10}{9}] or [{16}+{10}+{9}]
  equation?: string; // Will be wrapped in {} if field - Example: {15} for field number '15'
  showBeforeInput?: boolean;
  showEmptyDecimals?: boolean;
  ignoreHiddenFields?: boolean;
  insertAsText?: boolean;
  resultField?: string;
  decimalPlaces?: string; // Convert to number
  skipHide?: string;
  skipTo?: string;
};

export type JotformOperator =
  | 'equals'
  | 'notEquals'
  | 'contains'
  | 'notContains'
  | 'startsWith'
  | 'notStartsWith'
  | 'endsWith'
  | 'notEndsWith'
  | 'isEmpty'
  | 'isFilled';

export type JotformTerm = {
  id: string;
  field: string;
  operator: JotformOperator;
  value?: string; // Will be wrapped in {} if field - Example: {email} for field named 'email'
  isError: boolean;
};

export type JotformCondition = {
  action: string;
  id: string;
  index: string;
  link: 'Any' | 'All';
  terms: string;
  type: 'require' | 'mask' | 'field' | 'page' | 'calculation';
  priority: string;
  disabled?: '0' | '1';
};

const parseVisibilityAction = (
  action: JotformConditionalAction
): ConditionalFieldVisibilityAction[] => {
  const type = action.visibility?.includes('Hide') ? 'hide' : 'show';
  const actions: ConditionalFieldVisibilityAction[] = [];

  if (action.fields) {
    action.fields.forEach((field) => {
      actions.push({ type, field });
    });
  } else if (action.field) {
    actions.push({
      field: action.field,
      type,
    });
  }

  return actions;
};

const parseRequireAction = (action: JotformConditionalAction) => {
  let type: VisibilityActionType;

  switch (action.visibility) {
    case 'Enable':
      type = 'enable';
      break;
    case 'Disable':
      type = 'disable';
      break;
    case 'Require':
    case 'RequireMultiple':
      type = 'require';
      break;
    case 'Unrequire':
    case 'UnrequireMultiple':
      type = 'unrequire';
      break;
    default:
      type = 'enable';
  }
  const actions: ConditionalFieldVisibilityAction[] = [];

  if (action.fields) {
    action.fields.forEach((field) => {
      actions.push({ type, field });
    });
  } else if (action.field) {
    actions.push({
      field: action.field,
      type,
    });
  }

  return actions;
};

const parseMaskAction = (action: JotformConditionalAction) => {
  const type = 'mask';
  const actions: ConditionalFieldMaskAction[] = [];
  const mask = action.mask as string;

  if (action.fields) {
    action.fields.forEach((field) => {
      actions.push({ type, field, mask });
    });
  } else if (action.field) {
    actions.push({
      field: action.field,
      type,
      mask,
    });
  }

  return actions;
};

const parseCalculationAction = (action: JotformConditionalAction) => {
  const type = 'calculation';
  const actions: ConditionalFieldCalculationAction[] = [];
  const equation = action.equation as string;

  if (action.fields) {
    action.fields.forEach((field) => {
      actions.push({ type, field, equation });
    });
  } else if (action.field) {
    actions.push({
      field: action.field,
      type,
      equation,
    });
  }

  return actions;
};

const parsePageAction = (action: JotformConditionalAction) => {
  const type = action.skipHide === 'skipTo' ? 'navigate' : 'hide';
  const actions: ConditionalPageAction[] = [];
  const page = action.skipTo as string;

  actions.push({
    page,
    type,
  });

  return actions;
};

export const parseConditionals = (
  jotformConditionals: JotformCondition[]
): Conditional<ConditionalAction>[] => {
  const conditionals: Conditional<ConditionalAction>[] | any = jotformConditionals.map(
    (conditional) => {
      const actions: ConditionalAction[] = [];

      JSON.parse(conditional.action).forEach((action: JotformConditionalAction) => {
        if (conditional.disabled === '1') {
          return;
        }
        switch (conditional.type) {
          case 'field':
            actions.push(...parseVisibilityAction(action));
            break;
          case 'require':
            actions.push(...parseRequireAction(action));
            break;
          case 'mask':
            actions.push(...parseMaskAction(action));
            break;
          case 'calculation':
            actions.push(...parseCalculationAction(action));
            break;
          case 'page':
            actions.push(...parsePageAction(action));
            break;
          default:
        }
      });

      const terms: ConditionalTerm[] = JSON.parse(conditional.terms).map((term: JotformTerm) => {
        return {
          fieldId: term.field,
          operator: term.operator,
          checkValue: term.value,
        };
      });

      const termLink = conditional.link;

      return {
        terms,
        actions,
        termLink,
        priority: parseInt(conditional.priority, 10),
      };
    }
  );

  return conditionals;
};
